import { motion, AnimatePresence } from 'framer-motion';
import { NotionToggle } from '@/components/NotionToggle';
import { MemeDetector } from '@/components/MemeDetector';
import { LaundryPipeline } from '@/components/LaundryPipeline';
import { EmojiDictionary } from '@/components/EmojiDictionary';
import { ProjectManager } from '../ProjectManager';
import { memes } from '@/data/memes';
import { tasks } from '@/data/tasks';
import { useState, useEffect } from 'react';
import dorofeevHappy from '@/assets/dorofeev-happy.png';

interface NotionBlockProps {
  onNext?: () => void;
}

export function NotionBlock({ onNext }: NotionBlockProps) {
  const [checkedTasks, setCheckedTasks] = useState<number[]>([]);
  const [showReward, setShowReward] = useState(false);

  const toggleTask = (index: number) => {
    setCheckedTasks(prev => 
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
    );
  };

  useEffect(() => {
    if (checkedTasks.length === tasks.length && tasks.length > 0) {
      setShowReward(true);
      // Optional: auto-hide after some time
      // const timer = setTimeout(() => setShowReward(false), 5000);
      // return () => clearTimeout(timer);
    }
  }, [checkedTasks]);

  return (
    <motion.section
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-[#191919] text-white"
      aria-label="SELECT * FROM Vova - База Знаний"
    >
      {/* Cover */}
      <div className="relative h-48 sm:h-64 w-full overflow-hidden" aria-hidden="true">
        <div
          className="absolute inset-0 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 opacity-80"
          style={{
            backgroundImage: 'radial-gradient(circle at 30% 50%, rgba(255,255,255,0.3) 0%, transparent 50%)',
          }}
        >
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-6xl">🚲</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-8 py-8 sm:py-12 overflow-x-visible">
        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <span className="text-3xl sm:text-4xl" aria-hidden="true">🗄️</span>
          <h1 className="text-2xl sm:text-3xl font-bold text-white/90 font-mono">
            SELECT * FROM Vova
          </h1>
        </div>
        <p className="text-[#9B9A97] text-sm mb-8 ml-12 sm:ml-14 font-mono">
          -- База знаний v2.5
        </p>

        {/* Current Projects */}
        <div className="mb-8">
          <NotionToggle id="projects" title="Проекты" defaultOpen>
            <ProjectManager />
          </NotionToggle>
        </div>

        {/* Jedi Techniques Section */}
        <div className="mb-8">
          <NotionToggle id="jedi" title="Список задач на сегодня" defaultOpen>
            <div className="space-y-4">
              <p className="text-sm text-[#9B9A97] leading-relaxed">
                Согласно заветам Максима Дорофеева, любая задача должна начинаться с <span className="text-indigo-400">глагола</span>, 
                быть понятной даже пьяной обезьянке и не требовать лишнего мыслетоплива.
              </p>
              <div className="grid gap-2">
                {tasks.map((task, index) => (
                  <button
                    key={index}
                    onClick={() => toggleTask(index)}
                    className="flex items-center gap-3 p-2 rounded hover:bg-white/5 transition-colors group w-full text-left"
                  >
                    <div className={`w-5 h-5 border rounded flex-shrink-0 flex items-center justify-center transition-colors ${
                      checkedTasks.includes(index) 
                        ? 'bg-indigo-500 border-indigo-500' 
                        : 'border-[#4d4d4d] group-hover:border-indigo-500'
                    }`}>
                      {checkedTasks.includes(index) && (
                        <motion.svg
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="w-3.5 h-3.5 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </motion.svg>
                      )}
                    </div>
                    <span className={`text-sm transition-colors ${
                      checkedTasks.includes(index) ? 'text-[#9B9A97] line-through' : 'text-white/80 group-hover:text-white'
                    }`}>
                      {task}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </NotionToggle>
        </div>

        {/* Reward Animation */}
        <AnimatePresence>
          {showReward && (
            <motion.div
              initial={{ x: '-100vw', y: '100vh', rotate: -20, scale: 0.5 }}
              animate={{ 
                x: [null, '-60vw', '-20vw', '0vw'],
                y: [null, '20vh', '60vh', '0vh'],
                rotate: [null, -10, 5, 0],
                scale: [null, 0.7, 0.9, 1],
              }}
              transition={{ 
                duration: 2,
                times: [0, 0.3, 0.6, 1],
                ease: "easeOut"
              }}
              exit={{ opacity: 0, scale: 0.5 }}
              className="fixed inset-0 flex items-center justify-center z-50 cursor-pointer"
              onClick={() => setShowReward(false)}
            >
              <div className="relative">
                <motion.img 
                  src={dorofeevHappy} 
                  alt="Happy Dorofeev" 
                  className="w-64 sm:w-96 drop-shadow-[0_0_30px_rgba(99,102,241,0.5)]"
                  animate={{ 
                    y: [0, -50, 0, -30, 0, -10, 0],
                  }}
                  transition={{ 
                    duration: 2,
                    times: [0, 0.2, 0.4, 0.6, 0.8, 0.9, 1],
                    repeat: Infinity,
                    repeatDelay: 1
                  }}
                />
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.5 }}
                  className="absolute -bottom-10 left-1/2 -translate-x-1/2 bg-indigo-600 text-white px-6 py-2 rounded-full font-bold shadow-xl whitespace-nowrap"
                >
                  А теперь можно и ютубчик посмотреть
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Emoji Dictionary - NEW */}
        <div className="mb-8">
          <NotionToggle id="emoji" title="Кто не понял, тот поймёт" defaultOpen>
            <EmojiDictionary />
          </NotionToggle>
        </div>

        {/* Meme Database */}
        <div className="mb-8">
          <NotionToggle id="memes" title="Банк Мемов" defaultOpen>
            <ul className="space-y-2">
              {memes.map((meme) => (
                <li key={meme.id}>
                  <MemeDetector meme={meme}>
                    <span className="text-white/90 hover:text-white underline decoration-dotted cursor-help">
                      {meme.title}
                    </span>
                  </MemeDetector>
                </li>
              ))}
            </ul>
          </NotionToggle>
        </div>

        {/* Laundry Pipeline */}
        <div className="mb-8">
          <NotionToggle id="laundry" title="Пайплайн: Стирка и Логистика" defaultOpen>
            <LaundryPipeline />
          </NotionToggle>
        </div>
      </div>
    </motion.section>
  );
}
